import React, { useState, useEffect, useRef } from 'react';
import type { CurrentPage } from '../App';
import logoPath from '../assets/NOOR.png'; // Assuming you want to use the logo
import { Eye, EyeOff, User, Building2, Check, Zap, Crown, Users } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { secureLog, redactEmail } from '../utils/secureLogger'; // Import the Supabase client
import { AccountType, ACCOUNT_TYPE_OPTIONS } from '../types/account';
import { PlanType, formatPrice, formatBillingPeriod, getPlansForAccountType } from '../types/pricing';
import { createVerificationCode, verifyCode, resendVerificationCode, sendVerificationEmail, sendPasswordResetEmail, updatePasswordViaServer } from '../services/emailVerificationService';
import { checkPendingInvitation, acceptInvitation, createOrganization, getInvitationByToken } from '../services/teamService';
import type { TeamInvitationWithDetails } from '../types/team';
import { iapService, PRODUCT_IDS, type ProductId } from '../services/iapService';
import { sessionManagementService } from '../services/sessionManagementService';
import { remoteLogoutService } from '../services/remoteLogoutService';
import MultiDeviceConflictModal from './MultiDeviceConflictModal';
import { Capacitor } from '@capacitor/core';

interface AuthPageProps {
  onNavigate: (page: CurrentPage) => void;
  inviteToken?: string;
}

const assessorTypes = [
  "Energy Assessor",
  "Property Inventory Clerk",
  "Chartered Surveyor",
  "Valuer",
  "Retrofit Assessor",
  "Other",
];

const hearAboutUsOptions = [
  "Google Search",
  "Social Media",
  "Colleague/Friend",
  "Industry Event",
  "Professional Body",
  "App Store",
  "Referral",
  "Other",
];

const AuthPage: React.FC<AuthPageProps> = ({ onNavigate, inviteToken }) => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [assessorType, setAssessorType] = useState(assessorTypes[0]);
  const [assessorId, setAssessorId] = useState('');
  const [energyAccreditation, setEnergyAccreditation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  
  // Account type state
  const [accountType, setAccountType] = useState<AccountType>('personal');
  
  // Subscription plan state
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [showPlanFeaturesModal, setShowPlanFeaturesModal] = useState(false);
  
  // IAP purchase stage state
  const [showIAPSelection, setShowIAPSelection] = useState(false);
  const [purchasingIAP, setPurchasingIAP] = useState(false);
  const [iapPurchaseComplete, setIAPPurchaseComplete] = useState(false);
  
  // Email verification state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes in seconds
  
  // Password reset state
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  
  // Business account specific fields
  const [companyName, setCompanyName] = useState('');
  const [companyRegistration, setCompanyRegistration] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  
  // How did you hear about us fields
  const [hearAboutUs, setHearAboutUs] = useState('');
  const [referralDetails, setReferralDetails] = useState('');
  
  // Team invitation state
  const [pendingInvitation, setPendingInvitation] = useState<TeamInvitationWithDetails | null>(null);
  const [checkingInvitation, setCheckingInvitation] = useState(false);
  
  // Remote logout state
  const [showRemoteLogoutModal, setShowRemoteLogoutModal] = useState(false);
  const [remoteLogoutStep, setRemoteLogoutStep] = useState<'request' | 'confirm'>('request');
  const [remoteLogoutEmail, setRemoteLogoutEmail] = useState('');
  const [remoteLogoutCode, setRemoteLogoutCode] = useState('');
  const [remoteLogoutLoading, setRemoteLogoutLoading] = useState(false);
  const [remoteLogoutError, setRemoteLogoutError] = useState<string | null>(null);
  const [remoteLogoutTimeRemaining, setRemoteLogoutTimeRemaining] = useState(120); // 2 minutes
  const [resendingRemoteLogoutCode, setResendingRemoteLogoutCode] = useState(false);
  
  // Refs for form fields (for auto-scroll to errors)
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const assessorIdRef = useRef<HTMLInputElement>(null);
  const companyNameRef = useRef<HTMLInputElement>(null);
  const companyRegistrationRef = useRef<HTMLInputElement>(null);
  const errorDivRef = useRef<HTMLDivElement>(null);
  
  // Verification success state (shown within verification modal)
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Handle invitation token from URL
  useEffect(() => {
    const loadInvitation = async () => {
      if (inviteToken) {
        secureLog.debug('[Invitation] Loading invitation from token');
        setCheckingInvitation(true);
        
        try {
          const invitation = await getInvitationByToken(inviteToken);
          
          if (invitation) {
            secureLog.info('[Invitation] Invitation found');
            setPendingInvitation(invitation);
            
            // Pre-fill email and company name
            setEmail(invitation.invited_email);
            setCompanyName(invitation.organization_name || '');
            
            // Force signup view
            setIsLoginView(false);
            
            // No payment required for invited users
            setAccountType('personal'); // Invited users join as team members
          } else {
            secureLog.warn('[Invitation] Invitation not found or expired');
            setError('Invitation not found or has expired. Please contact your team admin.');
          }
        } catch (error) {
          secureLog.error('[Invitation] Error loading invitation', error);
          setError('Failed to load invitation. Please try again.');
        } finally {
          setCheckingInvitation(false);
        }
      }
    };
    
    loadInvitation();
  }, [inviteToken]);

  // Password validation
  const validatePassword = (pwd: string): { isValid: boolean; error: string | null } => {
    if (pwd.length < 8) {
      return { isValid: false, error: '🔒 Please choose a stronger password with at least 8 characters' };
    }
    if (!/[A-Z]/.test(pwd)) {
      return { isValid: false, error: '🔒 Please add at least one uppercase letter to make your password stronger' };
    }
    if (!/[a-z]/.test(pwd)) {
      return { isValid: false, error: '🔒 Please add at least one lowercase letter to make your password stronger' };
    }
    if (!/[0-9]/.test(pwd)) {
      return { isValid: false, error: '🔒 Please add at least one number to make your password stronger' };
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return { isValid: false, error: '🔒 Please add a special character (!@#$%^&*...) to make your password stronger' };
    }
    return { isValid: true, error: null };
  };

  // Handle forgot password click
  const handleForgotPassword = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      emailRef.current?.focus();
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      // Generate and send verification code
      const codeResult = await createVerificationCode(email);
      
      if (!codeResult.success || !codeResult.code) {
        setError(codeResult.error || 'Failed to send reset code');
        setLoading(false);
        return;
      }

      // Send code via password reset email (with proper styling)
      await sendPasswordResetEmail(email, codeResult.code);
      
      // Set up password reset mode
      setIsPasswordResetMode(true);
      setResetEmail(email);
      setTimeRemaining(120); // 2 minutes
      setShowVerificationModal(true);
      
      secureLog.email('Password reset code sent to', email);
    } catch (e) {
      secureLog.error('Forgot password error', e);
      setError('Failed to send password reset code');
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setVerificationError('Please enter a valid 6-digit code');
      return;
    }

    setVerificationLoading(true);
    setVerificationError(null);

    try {
      // Verify the code
      const emailToVerify = isPasswordResetMode ? resetEmail : pendingUserData.email;
      const result = await verifyCode(emailToVerify, verificationCode);
      
      if (!result.success) {
        setVerificationError(result.error || 'Invalid verification code');
        setVerificationLoading(false);
        return;
      }

      if (isPasswordResetMode) {
        // Password reset mode - show success and new password fields
        secureLog.info('✅ CODE VERIFIED - Ready for password reset');
        setVerificationSuccess(true);
        setSuccessMessage('Code verified! Please enter your new password.');
      } else {
        // Signup mode - Email verified! Now proceed to IAP purchase
        secureLog.info('✅ CODE VERIFIED - Proceeding to IAP purchase');
        
        // Close verification modal
        setShowVerificationModal(false);
        setVerificationSuccess(false);
        
        // Map selected plan to product ID
        if (!selectedPlan) {
          setError('No plan selected. Please try again.');
          return;
        }
        
        const planToProductId: Record<PlanType, ProductId> = {
          'standard': PRODUCT_IDS.STANDARD,
          'pro': PRODUCT_IDS.PRO,
          'pro_trial': PRODUCT_IDS.PRO_TRIAL,
          'team': PRODUCT_IDS.TEAM,
          'business': PRODUCT_IDS.BUSINESS,
        };
        
        const productId = planToProductId[selectedPlan];
        
        secureLog.info('[IAP] Auto-initiating purchase for selected plan:', selectedPlan, productId);
        
        // Show IAP processing modal
        setShowIAPSelection(true);
        const successMsg = pendingInvitation 
          ? `Welcome to ${pendingInvitation.organization_name}! Processing your subscription...`
          : 'Email verified! Processing your subscription...';
        setSuccessMessage(successMsg);
        
        // Automatically trigger IAP purchase
        await handleIAPPurchase(productId);
      }
      
      setVerificationError(null);
    } catch (e) {
      secureLog.error('Verification error', e);
      setVerificationError('An error occurred during verification');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle resend verification code
  const handleResendCode = async () => {
    setResendingCode(true);
    setVerificationError(null);

    try {
      const emailToResend = isPasswordResetMode ? resetEmail : pendingUserData.email;
      const result = await resendVerificationCode(emailToResend);
      
      if (!result.success || !result.code) {
        setVerificationError(result.error || 'Failed to resend code');
        setResendingCode(false);
        return;
      }

      // Send new code via appropriate email template
      if (isPasswordResetMode) {
        await sendPasswordResetEmail(emailToResend, result.code);
      } else {
        await sendVerificationEmail(emailToResend, result.code);
      }
      
      // Reset timer and clear entered code
      setTimeRemaining(120); // 2 minutes
      setVerificationCode('');
      setVerificationError(null);
      
      // Show success message
      const mode = isPasswordResetMode ? 'password reset' : 'verification';
      alert(`A new ${mode} code has been sent to your email!`);
    } catch (e) {
      secureLog.error('Resend error', e);
      setVerificationError('Failed to resend code');
    } finally {
      setResendingCode(false);
    }
  };

  // Handle password reset submission
  const handleResetPassword = async () => {
    // Validate new passwords
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setVerificationError(passwordValidation.error);
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setVerificationError("Passwords don't match!");
      return;
    }

    setVerificationLoading(true);
    setVerificationError(null);

    try {
      // Update password via server admin API (bypasses recovery email)
      const result = await updatePasswordViaServer(resetEmail, newPassword);
      
      if (!result.success) {
        setVerificationError(result.error || 'Failed to reset password. Please try again.');
        setVerificationLoading(false);
        return;
      }

      // Success! Close modal and show success message
      setShowVerificationModal(false);
      setIsPasswordResetMode(false);
      setVerificationSuccess(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setVerificationCode('');
      setResetEmail('');
      
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
      setError(null);
      secureLog.info('✅ Password reset successful');
    } catch (e) {
      secureLog.error('Password reset error', e);
      setVerificationError('Failed to reset password');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle IAP purchase
  const handleIAPPurchase = async (productId: ProductId) => {
    setPurchasingIAP(true);
    setError(null);
    
    try {
      secureLog.info('[IAP] Initiating purchase:', productId);
      
      // Initialize IAP service
      await iapService.initialize();
      
      // Attempt purchase
      const result = await iapService.purchase(productId);
      
      if (!result.success) {
        setError(result.error || 'Purchase failed. Please try again.');
        setPurchasingIAP(false);
        return;
      }
      
      // Purchase successful - proceed to account creation
      secureLog.info('[IAP] ✅ Purchase successful - creating account');
      setIAPPurchaseComplete(true);
      setPurchasingIAP(false);
      
      // Create account immediately after successful purchase
      await createAccountAfterPurchase();
    } catch (error: any) {
      secureLog.error('[IAP] Purchase error:', error);
      setError(error.message || 'An error occurred during purchase');
      setPurchasingIAP(false);
    }
  };
  
  // Create account after email verification and IAP purchase
  const createAccountAfterPurchase = async () => {
    if (!pendingUserData) {
      setError('Session expired. Please try again.');
      return;
    }
    
    setCreatingAccount(true);
    sessionStorage.setItem('creatingAccount', 'true');
    
    try {
      secureLog.info('[Signup] Creating account after IAP purchase');
      
      const { email, password, metadata } = pendingUserData;
      
      // Get current URL for email redirect
      const redirectUrl = window.location.origin;
      
      const signupOptions: any = {
        emailRedirectTo: `${redirectUrl}?verified=true${pendingInvitation ? `&invite=${inviteToken}` : ''}`,
        data: metadata
      };
      
      // For invited users, skip email confirmation requirement
      if (pendingInvitation) {
        signupOptions.emailRedirectTo = redirectUrl;
        secureLog.info('[Signup] Creating account for invited user');
      }
      
      // Create Supabase account
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: signupOptions
      });
      
      if (signUpError) {
        setError(signUpError.message);
        setCreatingAccount(false);
        sessionStorage.removeItem('creatingAccount');
        return;
      }
      
      if (!signUpData.user) {
        setError('Failed to create account');
        setCreatingAccount(false);
        sessionStorage.removeItem('creatingAccount');
        return;
      }
      
      secureLog.info('[Signup] ✅ Account created:', signUpData.user.id);
      
      // If there's a pending invitation, accept it
      if (pendingInvitation && signUpData.user.id) {
        try {
          await acceptInvitation(inviteToken || '', signUpData.user.id);
          secureLog.info('[Signup] ✅ Invitation accepted');
        } catch (invError) {
          secureLog.error('[Signup] Failed to accept invitation', invError);
        }
      } else {
        // Create organization for non-invitation signups
        try {
          const orgName = metadata.account_type === 'business' && metadata.company_name
            ? metadata.company_name
            : `${metadata.first_name} ${metadata.last_name}'s Organization`;
          
          // Map PlanType to SubscriptionPlan
          const subscriptionPlan: 'standard' | 'team' | 'business' | 'personal_pro' = 
            metadata.subscription_plan === 'pro' ? 'personal_pro' : 
            metadata.subscription_plan === 'pro_trial' ? 'personal_pro' :
            metadata.subscription_plan === 'team' ? 'team' :
            metadata.subscription_plan === 'business' ? 'business' :
            'standard';
          
          await createOrganization(orgName, subscriptionPlan, metadata.company_name);
          secureLog.info('[Signup] ✅ Organization created');
        } catch (orgError) {
          secureLog.error('[Signup] Failed to create organization', orgError);
        }
      }
      
      // Sign out immediately (user will login manually)
      await supabase.auth.signOut();
      
      // Clear session flag
      sessionStorage.removeItem('creatingAccount');
      
      // Close IAP modal and show success
      setShowIAPSelection(false);
      setIAPPurchaseComplete(false);
      setIsLoginView(true);
      
      // Clear form data
      clearSignupFields();
      setPendingUserData(null);
      setPendingInvitation(null);
      setSelectedPlan(null);
      
      setSuccessMessage('Account created successfully! Please log in with your credentials.');
      setError(null);
      setCreatingAccount(false);
      
      secureLog.info('[Signup] ✅ Signup flow complete - user ready to login');
    } catch (error: any) {
      secureLog.error('[Signup] Account creation failed:', error);
      setError(`Failed to create account: ${error.message}`);
      setCreatingAccount(false);
      sessionStorage.removeItem('creatingAccount');
    }
  };
  
  // Clean up any stale account creation flag on mount (handles page refresh during signup)
  useEffect(() => {
    sessionStorage.removeItem('creatingAccount');
  }, []);

  // Remote logout handlers
  const handleRequestRemoteLogout = async () => {
    if (!remoteLogoutEmail || !/\S+@\S+\.\S+/.test(remoteLogoutEmail)) {
      setRemoteLogoutError('Please enter a valid email address');
      return;
    }

    setRemoteLogoutLoading(true);
    setRemoteLogoutError(null);

    const result = await remoteLogoutService.requestRemoteLogout(remoteLogoutEmail);

    if (result.success) {
      setRemoteLogoutStep('confirm');
      setRemoteLogoutError(null);
      setRemoteLogoutTimeRemaining(120); // Reset timer to 2 minutes
    } else {
      setRemoteLogoutError(result.error || 'Failed to send confirmation code');
    }

    setRemoteLogoutLoading(false);
  };

  const handleResendRemoteLogoutCode = async () => {
    setResendingRemoteLogoutCode(true);
    setRemoteLogoutError(null);

    const result = await remoteLogoutService.requestRemoteLogout(remoteLogoutEmail);

    if (result.success) {
      setRemoteLogoutTimeRemaining(120); // Reset timer to 2 minutes
      setRemoteLogoutCode(''); // Clear the code field
    } else {
      setRemoteLogoutError(result.error || 'Failed to resend code');
    }

    setResendingRemoteLogoutCode(false);
  };

  const handleConfirmRemoteLogout = async () => {
    if (!remoteLogoutCode || remoteLogoutCode.length !== 6) {
      setRemoteLogoutError('Please enter the 6-digit code');
      return;
    }

    setRemoteLogoutLoading(true);
    setRemoteLogoutError(null);

    const result = await remoteLogoutService.confirmRemoteLogout(remoteLogoutEmail, remoteLogoutCode);

    if (result.success) {
      setShowRemoteLogoutModal(false);
      setRemoteLogoutStep('request');
      setRemoteLogoutCode('');
      setError(null);
      setSuccessMessage('All devices logged out successfully! You can now log in.');
    } else {
      setRemoteLogoutError(result.error || 'Invalid or expired code');
    }

    setRemoteLogoutLoading(false);
  };

  // Timer countdown for verification code
  useEffect(() => {
    if (showVerificationModal && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showVerificationModal, timeRemaining]);

  // Timer countdown for remote logout code
  useEffect(() => {
    if (remoteLogoutStep === 'confirm' && remoteLogoutTimeRemaining > 0) {
      const timer = setInterval(() => {
        setRemoteLogoutTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [remoteLogoutStep, remoteLogoutTimeRemaining]);

  // Check for pending team invitation when email changes (signup mode only)
  // Skip if we already have invitation from token (prevents overwriting)
  useEffect(() => {
    const checkInvitation = async () => {
      // Skip if we already loaded invitation from token
      if (inviteToken) {
        secureLog.debug('[Invitation] Skipping email check - already have invitation from token');
        return;
      }
      
      if (!isLoginView && email && email.includes('@')) {
        setCheckingInvitation(true);
        try {
          const invitation = await checkPendingInvitation(email);
          setPendingInvitation(invitation);
        } catch (error) {
          secureLog.error('Error checking invitation', error);
        } finally {
          setCheckingInvitation(false);
        }
      }
    };

    // Debounce the check
    const timer = setTimeout(() => {
      checkInvitation();
    }, 500);

    return () => clearTimeout(timer);
  }, [email, isLoginView, inviteToken]);

  // Format time remaining
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const clearSignupFields = () => {
    setFirstName('');
    setLastName('');
    setDateOfBirth('');
    setAssessorType(assessorTypes[0]);
    setAssessorId('');
    setEnergyAccreditation('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setAccountType('personal');
    setSelectedPlan(null);
    setCompanyName('');
    setCompanyRegistration('');
    setVatNumber('');
    setHearAboutUs('');
    setReferralDetails('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage('');

    // Block desktop signup - only allow on iOS
    if (!isLoginView && !Capacitor.isNativePlatform()) {
      setError('Account creation is only available on the iOS app. Please download the NoorCAD app from the App Store.');
      setLoading(false);
      return;
    }

    if (isLoginView) {
      // STEP 1: Check for existing sessions BEFORE attempting login
      console.log('[MULTI_DEVICE] Checking for existing sessions before login...');
      const sessionCheck = await sessionManagementService.checkExistingSession(email);
      
      if (sessionCheck.hasExisting) {
        console.log('[MULTI_DEVICE] ⚠️ Active session detected - showing modal');
        setLoading(false);
        setShowRemoteLogoutModal(true);
        setRemoteLogoutEmail(email);
        return; // Don't proceed with login - wait for user confirmation
      }

      console.log('[MULTI_DEVICE] ✅ No existing sessions - proceeding with login');
      
      // STEP 2: Proceed with login - no other sessions exist
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (signInError) {
        setError(signInError.message);
        secureLog.error('Login error', signInError);
        // Scroll to error message
        setTimeout(() => {
          errorDivRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // If it's a password error, focus password field
          if (signInError.message.toLowerCase().includes('password')) {
            passwordRef.current?.focus();
          } else {
            emailRef.current?.focus();
          }
        }, 100);
      } else if (data.user) {
        secureLog.user('Login successful', data.user);
        
        // CRITICAL: Register session BEFORE clearing loading state
        // This ensures the session exists in DB before setupSessionMonitoring() runs
        try {
          await sessionManagementService.registerSession();
          console.log('[MULTI_DEVICE] ✅ Session registered on this device');
        } catch (err) {
          console.error('[MULTI_DEVICE] Session registration failed:', err);
          // Continue anyway - monitoring will handle it
        }
        
        // Now clear loading state and let App.tsx handle navigation
        setLoading(false);
        return;
      } else {
        setError('Login failed. Please try again.');
        setLoading(false);
        return;
      }
      setLoading(false);
      return;
    }

    // Sign-up logic - Step 1: Validate and prepare for email verification
    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      setPasswordError(passwordValidation.error);
      setLoading(false);
      // Scroll to password field
      setTimeout(() => {
        passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        passwordRef.current?.focus();
      }, 100);
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords don't match!");
      setLoading(false);
      // Scroll to confirm password field
      setTimeout(() => {
        confirmPasswordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        confirmPasswordRef.current?.focus();
      }, 100);
      return;
    }
    // Only validate Assessor ID if it's required for this assessor type
    if (!['Property Inventory Clerk', 'Valuer', 'Other'].includes(assessorType) && !assessorId.trim()) {
        setError("Please enter your Assessor ID.");
        setLoading(false);
        // Scroll to assessor ID field
        setTimeout(() => {
          assessorIdRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          assessorIdRef.current?.focus();
        }, 100);
        return;
    }
    
    // Business account validation
    if (accountType === 'business') {
      if (!companyName.trim()) {
        setError("Please enter your company name.");
        setLoading(false);
        // Scroll to company name field
        setTimeout(() => {
          companyNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          companyNameRef.current?.focus();
        }, 100);
        return;
      }
      if (!companyRegistration.trim()) {
        setError("Please enter your company registration number.");
        setLoading(false);
        // Scroll to company registration field
        setTimeout(() => {
          companyRegistrationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          companyRegistrationRef.current?.focus();
        }, 100);
        return;
      }
    }
    
    // Require plan selection
    if (!selectedPlan) {
      setError("Please select a subscription plan.");
      setLoading(false);
      return;
    }
    
    // Require 'How did you hear about us?'
    if (!hearAboutUs.trim()) {
      setError("Please tell us how you heard about NoorCAD.");
      setLoading(false);
      return;
    }
    
    // If 'Referral' is selected, require referral details
    if (hearAboutUs === 'Referral' && !referralDetails.trim()) {
      setError("Please provide referral details (name of person/company who referred you).");
      setLoading(false);
      return;
    }

    try {
      // Prepare user metadata
      const initialUserMetadata: any = {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
        assessor_type: assessorType,
        assessor_id: assessorId.trim(),
        account_type: accountType,
      };
      
      // Add energy accreditation if Energy Assessor or Retrofit Assessor is selected
      if ((assessorType === 'Energy Assessor' || assessorType === 'Retrofit Assessor') && energyAccreditation) {
        initialUserMetadata.energy_accreditation = energyAccreditation;
      }
      
      // Add subscription plan
      initialUserMetadata.subscription_plan = selectedPlan;
      
      // Add hear_about_us and referral_details
      initialUserMetadata.hear_about_us = hearAboutUs.trim();
      if (hearAboutUs === 'Referral' && referralDetails.trim()) {
        initialUserMetadata.referral_details = referralDetails.trim();
      }
      
      // Add business-specific fields if business account or Personal Pro with data
      if (accountType === 'business') {
        initialUserMetadata.company_name = companyName.trim();
        initialUserMetadata.company_registration = companyRegistration.trim();
        if (vatNumber.trim()) {
          initialUserMetadata.vat_number = vatNumber.trim();
        }
      } else if (accountType === 'personal' && selectedPlan === 'pro') {
        // Personal Pro: only save if user provided data (optional)
        if (companyName.trim()) {
          initialUserMetadata.company_name = companyName.trim();
        }
        if (companyRegistration.trim()) {
          initialUserMetadata.company_registration = companyRegistration.trim();
        }
        if (vatNumber.trim()) {
          initialUserMetadata.vat_number = vatNumber.trim();
        }
      }

      // Store pending user data - DO NOT create account yet
      setPendingUserData({
        email,
        password,
        metadata: initialUserMetadata
      });
      
      // Generate and send verification code
      secureLog.info('[Signup] Sending email verification code');
      const codeResult = await createVerificationCode(email);
      
      if (!codeResult.success || !codeResult.code) {
        setError(codeResult.error || 'Failed to send verification code');
        setLoading(false);
        return;
      }
      
      // Send verification email
      await sendVerificationEmail(email, codeResult.code);
      
      // Show verification modal
      setTimeRemaining(120); // 2 minutes
      setShowVerificationModal(true);
      setLoading(false);
      
      secureLog.info('[Signup] ✅ Verification email sent - awaiting code entry');
    } catch (e) {
      secureLog.error('Unexpected error during signup preparation', e);
      setError(`An unexpected error occurred: ${(e as Error).message}`);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col items-center justify-center p-4 py-8 relative overflow-hidden">
      {/* Decorative gradient orbs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-amber-200/30 to-orange-200/30 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          {/* Logo with enhanced styling */}
          <div className="mb-6">
            <img 
              src={logoPath} 
              alt="NoorCAD Logo" 
              className="mx-auto h-16 sm:h-20 w-auto drop-shadow-xl" 
            />
          </div>
          
          {/* Modern gradient header text */}
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 bg-clip-text text-transparent drop-shadow-sm mb-3">
            {isLoginView ? 'Welcome Back!' : 'Create Your Account'}
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
            {isLoginView ? 'Sign in to continue' : 'Get started with your free account'}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-gradient-to-br from-white via-slate-50/50 to-white backdrop-blur-sm shadow-2xl rounded-2xl p-6 sm:p-8 space-y-5 border border-slate-200/60"
        >
          {/* Login Button - Show at top when in signup view */}
          {!isLoginView && (
            <div className="mb-2">
              <button
                type="button"
                onClick={() => {
                  setIsLoginView(true);
                  setError(null);
                  setSuccessMessage('');
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Login
              </button>
            </div>
          )}

          {successMessage && (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 text-green-700 rounded-xl text-sm font-medium shadow-md flex items-start gap-3">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{successMessage}</span>
            </div>
          )}

          {error && (
            <div ref={errorDivRef} className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-700 rounded-xl text-sm font-medium shadow-md">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p>{error}</p>
                  {error.includes('already logged in') && (
                    <button
                      onClick={() => {
                        setShowRemoteLogoutModal(true);
                        setRemoteLogoutStep('request');
                        setRemoteLogoutError(null);
                      }}
                      className="mt-3 px-4 py-2 bg-white hover:bg-red-50 text-red-700 font-semibold rounded-lg border-2 border-red-300 hover:border-red-400 transition-all text-xs"
                    >
                      🔓 Logout from other device
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Email Input - Common to both Login and Sign Up */}
          <div className="relative group">
            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
              Email address {!isLoginView && pendingInvitation && <span className="text-xs text-green-600">(from invitation)</span>}
            </label>
            <div className="relative">
              <input
                ref={emailRef}
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || (!isLoginView && !!pendingInvitation)}
                className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 transition-all duration-200 bg-white hover:border-slate-300"
                placeholder="you@example.com"
              />
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity blur-sm" />
            </div>
          </div>

          {/* Company Name Field - Read Only for Invited Users */}
          {!isLoginView && pendingInvitation && (
            <div className="relative group">
              <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                Company/Organization <span className="text-xs text-green-600">(from invitation)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="company"
                  value={pendingInvitation.organization_name || ''}
                  disabled
                  className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm sm:text-sm bg-slate-50 text-slate-500 transition-all duration-200"
                  readOnly
                />
              </div>
            </div>
          )}

          {/* Team Invitation Banner */}
          {!isLoginView && pendingInvitation && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-green-100 rounded-lg">
                  <Users size={20} className="text-green-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-green-900 mb-1">
                    🎉 You're Invited to Join a Team!
                  </p>
                  <p className="text-sm text-green-800">
                    <strong>{pendingInvitation.organization_name}</strong> has invited you to join their team.
                    Complete your signup to get <strong>free access</strong> under their plan!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Password Input - Common to both Login and Sign Up */}
          <div className="relative group">
            <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
            <div className="relative">
              <input
                ref={passwordRef}
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="appearance-none block w-full px-4 py-3 pr-12 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 transition-all duration-200 bg-white hover:border-slate-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                disabled={loading}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-sm leading-5 text-slate-500 hover:text-amber-600 disabled:opacity-50 transition-colors duration-200"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity blur-sm" />
            </div>
            {!isLoginView && (
              <div className="mt-2 text-xs space-y-1">
                <p className="font-semibold text-slate-700">Password must contain:</p>
                <ul className="space-y-1 text-slate-600">
                  <li className={`flex items-center gap-1 ${password.length >= 8 ? 'text-green-600' : ''}`}>
                    {password.length >= 8 ? '✓' : '○'} At least 8 characters
                  </li>
                  <li className={`flex items-center gap-1 ${/[A-Z]/.test(password) ? 'text-green-600' : ''}`}>
                    {/[A-Z]/.test(password) ? '✓' : '○'} One uppercase letter
                  </li>
                  <li className={`flex items-center gap-1 ${/[a-z]/.test(password) ? 'text-green-600' : ''}`}>
                    {/[a-z]/.test(password) ? '✓' : '○'} One lowercase letter
                  </li>
                  <li className={`flex items-center gap-1 ${/[0-9]/.test(password) ? 'text-green-600' : ''}`}>
                    {/[0-9]/.test(password) ? '✓' : '○'} One number
                  </li>
                  <li className={`flex items-center gap-1 ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600' : ''}`}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : '○'} One special character (!@#$%...)
                  </li>
                </ul>
              </div>
            )}
          </div>

          {!isLoginView && (
            <>
              {/* Confirm Password - Only for Sign Up */}
              <div className="relative group">
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    ref={confirmPasswordRef}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required={!isLoginView}
                    disabled={loading}
                    className="appearance-none block w-full px-4 py-3 pr-12 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500 transition-all duration-200 bg-white hover:border-slate-300"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-sm leading-5 text-slate-500 hover:text-amber-600 disabled:opacity-50 transition-colors duration-200"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  <div className="absolute inset-0 -z-10 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl opacity-0 group-focus-within:opacity-100 transition-opacity blur-sm" />
                </div>
              </div>

              {/* Account Type Selection - Hidden for invited users */}
              {!pendingInvitation && (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-700 mb-3">Account Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ACCOUNT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setAccountType(option.value)}
                      disabled={loading}
                      className={`relative group p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                        accountType === option.value
                          ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-orange-50 shadow-md'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`text-3xl transition-transform duration-200 ${
                          accountType === option.value ? 'scale-110' : 'group-hover:scale-105'
                        }`}>
                          {option.value === 'personal' ? <User size={28} className="text-blue-600" /> : <Building2 size={28} className="text-purple-600" />}
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-bold text-sm mb-1 ${
                            accountType === option.value ? 'text-amber-700' : 'text-slate-800'
                          }`}>
                            {option.label}
                          </h3>
                          <p className={`text-xs ${
                            accountType === option.value ? 'text-amber-600' : 'text-slate-600'
                          }`}>
                            {option.description}
                          </p>
                        </div>
                        {accountType === option.value && (
                          <div className="absolute top-2 right-2">
                            <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Pricing Plan Selection - Hidden for invited users */}
              {!pendingInvitation && (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-700 mb-3">Choose Your Plan</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {getPlansForAccountType(accountType).map((plan) => (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => setSelectedPlan(plan.id)}
                      disabled={loading}
                      className={`relative group p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                        selectedPlan === plan.id
                          ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg ring-2 ring-green-200'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
                            <Crown size={12} />
                            POPULAR
                          </span>
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        {/* Plan Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className={`font-bold text-base ${
                              selectedPlan === plan.id ? 'text-green-700' : 'text-slate-800'
                            }`}>
                              {plan.name}
                            </h3>
                            {plan.userCount && (
                              <p className="text-xs text-slate-600 mt-0.5">{plan.userCount}</p>
                            )}
                          </div>
                          {selectedPlan === plan.id && (
                            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check size={14} className="text-white" />
                            </div>
                          )}
                        </div>
                        
                        {/* Price */}
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl font-bold ${
                            selectedPlan === plan.id ? 'text-green-600' : 'text-slate-900'
                          }`}>
                            {formatPrice(plan.price)}
                          </span>
                          <span className="text-sm text-slate-600">{formatBillingPeriod(plan.billingPeriod)}</span>
                        </div>
                        
                        {/* Description */}
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {plan.description}
                        </p>
                        
                        {/* Key Features */}
                        <div className="space-y-1.5 pt-2 border-t border-slate-200">
                          {plan.features.slice(0, 3).map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check size={14} className={`flex-shrink-0 mt-0.5 ${
                                selectedPlan === plan.id ? 'text-green-600' : 'text-blue-600'
                              }`} />
                              <span className="text-xs text-slate-700">{feature}</span>
                            </div>
                          ))}
                          {plan.features.length > 3 && (
                            <p className="text-xs text-slate-500 italic pl-5">
                              +{plan.features.length - 3} more features
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex flex-col items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setShowPlanFeaturesModal(true)}
                    disabled={loading}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline decoration-2 underline-offset-2 transition-colors duration-200 disabled:opacity-50"
                  >
                    📋 View All Features & Compare Plans
                  </button>
                  <p className="text-xs text-slate-500 text-center italic">
                    💡 Don't worry - you can change your plan anytime in Settings
                  </p>
                </div>
              </div>
              )}

              {/* Business Account Fields - Hidden for invited users */}
              {!pendingInvitation && (accountType === 'business' || (accountType === 'personal' && selectedPlan === 'pro')) && (
                <>
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-purple-900 mb-3 flex items-center gap-2">
                      <Building2 size={16} className="text-purple-600" />
                      Business Information {accountType === 'personal' && <span className="text-xs font-normal text-purple-600">(Optional for Pro)</span>}
                    </h3>
                    <div className="space-y-4">
                      <div className="relative group">
                        <label htmlFor="companyName" className="block text-sm font-semibold text-slate-700 mb-2">Company Name {accountType === 'business' ? '*' : ''}</label>
                        <input
                          ref={companyNameRef}
                          type="text"
                          id="companyName"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          required={accountType === 'business'}
                          disabled={loading}
                          className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                          placeholder="e.g., NOOR Energy Assessments Ltd"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative group">
                          <label htmlFor="companyRegistration" className="block text-sm font-semibold text-slate-700 mb-2">Company Registration {accountType === 'business' ? '*' : ''}</label>
                          <input
                            ref={companyRegistrationRef}
                            type="text"
                            id="companyRegistration"
                            value={companyRegistration}
                            onChange={(e) => setCompanyRegistration(e.target.value)}
                            required={accountType === 'business'}
                            disabled={loading}
                            className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                            placeholder="e.g., 12345678"
                          />
                        </div>
                        
                        <div className="relative group">
                          <label htmlFor="vatNumber" className="block text-sm font-semibold text-slate-700 mb-2">VAT Number</label>
                          <input
                            type="text"
                            id="vatNumber"
                            value={vatNumber}
                            onChange={(e) => setVatNumber(e.target.value)}
                            disabled={loading}
                            className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                            placeholder="e.g., GB123456789"
                          />
                          <p className="text-xs text-slate-500 mt-1">Optional</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Personal Information Header */}
              <div className="border-t-2 border-slate-200 pt-4">
                <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <User size={16} className="text-blue-600" />
                  Personal Information
                </h3>
              </div>

              {/* Other Sign Up Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative group">
                  <label htmlFor="firstName" className="block text-sm font-semibold text-slate-700 mb-2">First Name</label>
                  <input 
                    type="text" 
                    id="firstName" 
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)} 
                    required 
                    disabled={loading} 
                    className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300" 
                    placeholder="John" 
                  />
                </div>
                <div className="relative group">
                  <label htmlFor="lastName" className="block text-sm font-semibold text-slate-700 mb-2">Last Name</label>
                  <input 
                    type="text" 
                    id="lastName" 
                    value={lastName} 
                    onChange={(e) => setLastName(e.target.value)} 
                    required 
                    disabled={loading} 
                    className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300" 
                    placeholder="Doe" 
                  />
                </div>
              </div>
              
              <div className="relative group">
                <label htmlFor="dateOfBirth" className="block text-sm font-semibold text-slate-700 mb-2">Date of Birth</label>
                <input 
                  type="date" 
                  id="dateOfBirth" 
                  value={dateOfBirth} 
                  onChange={(e) => setDateOfBirth(e.target.value)} 
                  required 
                  disabled={loading} 
                  className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300" 
                />
              </div>
              
              <div className="relative group">
                <label htmlFor="assessorType" className="block text-sm font-semibold text-slate-700 mb-2">Assessor Type</label>
                <select 
                  id="assessorType" 
                  value={assessorType} 
                  onChange={(e) => setAssessorType(e.target.value)} 
                  required 
                  disabled={loading} 
                  className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                >
                  {assessorTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>

              {/* Assessor ID Field - Conditionally shown based on assessor type */}
              {!['Property Inventory Clerk', 'Valuer', 'Other'].includes(assessorType) && (
                <div className="relative group">
                  <label htmlFor="assessorId" className="block text-sm font-semibold text-slate-700 mb-2">Assessor ID</label>
                  <input
                    ref={assessorIdRef}
                    type="text"
                    id="assessorId"
                    value={assessorId}
                    onChange={(e) => setAssessorId(e.target.value)}
                    required
                    disabled={loading}
                    className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                    placeholder="e.g., QUID123456"
                  />
                </div>
              )}

              {/* Energy Accreditation Field - For Energy Assessors and Retrofit Assessors */}
              {(assessorType === 'Energy Assessor' || assessorType === 'Retrofit Assessor') && (
                <div className="relative group">
                  <label htmlFor="energyAccreditation" className="block text-sm font-semibold text-slate-700 mb-2">Energy Accreditation Body</label>
                  <select
                    id="energyAccreditation"
                    value={energyAccreditation}
                    onChange={(e) => setEnergyAccreditation(e.target.value)}
                    disabled={loading}
                    className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                  >
                    <option value="">Select Accreditation Body</option>
                    <option value="Elmhurst Energy">Elmhurst Energy</option>
                    <option value="Quidos">Quidos</option>
                    <option value="Stroma">Stroma</option>
                    <option value="ECMK">ECMK</option>
                    <option value="Rics">Rics</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}

              {/* How did you hear about us? - MANDATORY */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                <h3 className="text-sm font-bold text-blue-900 mb-3">How did you hear about NoorCAD? *</h3>
                <div className="space-y-4">
                  <div className="relative group">
                    <select
                      id="hearAboutUs"
                      value={hearAboutUs}
                      onChange={(e) => {
                        setHearAboutUs(e.target.value);
                        if (e.target.value !== 'Referral') {
                          setReferralDetails('');
                        }
                      }}
                      required
                      disabled={loading}
                      className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                    >
                      <option value="">Select an option...</option>
                      {hearAboutUsOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {hearAboutUs === 'Referral' && (
                    <div className="relative group">
                      <label htmlFor="referralDetails" className="block text-sm font-semibold text-slate-700 mb-2">
                        Who referred you? * <span className="text-xs font-normal text-slate-500">(Name or Company)</span>
                      </label>
                      <input
                        type="text"
                        id="referralDetails"
                        value={referralDetails}
                        onChange={(e) => setReferralDetails(e.target.value)}
                        required
                        disabled={loading}
                        className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white hover:border-slate-300"
                        placeholder="e.g., John Smith or ABC Energy Services"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Submit Button - Modern Gradient Design */}
          <div className="pt-2">
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full group relative flex justify-center items-center gap-2 py-3.5 px-6 border-2 border-transparent rounded-xl shadow-lg text-base font-bold text-white bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 hover:from-amber-600 hover:via-orange-600 hover:to-amber-700 focus:outline-none focus:ring-4 focus:ring-amber-500/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-md overflow-hidden"
            >
              {/* Shine effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              
              {/* Button text with loading spinner */}
              {loading && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span className="drop-shadow-sm relative z-10">
                {loading ? (isLoginView ? 'Signing In...' : 'Creating Account...') : (isLoginView ? 'Sign In' : 'Create Account')}
              </span>
            </button>
          </div>

          {/* Forgot Password Link - Only for Login */}
          {isLoginView && (
            <div className="text-sm text-center">
              <a 
                href="#" 
                onClick={(e) => { 
                  e.preventDefault(); 
                  handleForgotPassword(); 
                }} 
                className="font-semibold text-amber-600 hover:text-orange-600 transition-colors duration-200 hover:underline decoration-2 underline-offset-2"
              >
                Forgot your password?
              </a>
            </div>
          )}
        </form>

        {/* Sign Up Link - Only show for normal login (not for invites) */}
        {!inviteToken && isLoginView && (
          <div className="mt-8 text-center">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gradient-to-r from-transparent via-slate-50 to-transparent text-slate-600 font-medium">
                  Don't have an account?
                </span>
              </div>
            </div>
            
{Capacitor.isNativePlatform() ? (
              <>
                <button
                  onClick={() => {
                    setIsLoginView(false);
                    setError(null);
                    setSuccessMessage('');
                  }}
                  className="mt-4 inline-block px-6 py-2.5 rounded-xl font-bold text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white border-2 border-green-400 hover:from-green-600 hover:to-emerald-700 hover:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-300/50 transform hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  ✨ Create Account
                </button>
                <p className="mt-3 text-xs text-slate-500">
                  Sign up directly in the app to get started
                </p>
              </>
            ) : (
              <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                <p className="text-sm text-blue-900 text-center font-semibold mb-2">
                  📱 iOS App Required
                </p>
                <p className="text-xs text-blue-700 text-center">
                  Account creation is only available on the iOS app. Download NoorCAD from the App Store to sign up.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Plan Features Modal */}
      {showPlanFeaturesModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Zap size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">
                    {accountType === 'personal' ? 'Individual Plans' : 'Team Plans'} - Full Features
                  </h2>
                  <p className="text-blue-100 text-sm mt-0.5">Compare all features and choose the best plan for you</p>
                </div>
              </div>
              <button
                onClick={() => setShowPlanFeaturesModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {getPlansForAccountType(accountType).map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative rounded-xl border-2 p-6 transition-all duration-200 ${
                      selectedPlan === plan.id
                        ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                    }`}
                  >
                    {/* Popular Badge */}
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md">
                          <Crown size={12} />
                          POPULAR
                        </span>
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="mb-4 pb-4 border-b border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`text-2xl font-bold ${
                          selectedPlan === plan.id ? 'text-green-700' : 'text-slate-800'
                        }`}>
                          {plan.name}
                        </h3>
                        {selectedPlan === plan.id && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                            <Check size={12} />
                            Selected
                          </div>
                        )}
                      </div>
                      {plan.userCount && (
                        <p className="text-sm text-slate-600 mb-2">{plan.userCount}</p>
                      )}
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className={`text-3xl font-bold ${
                          selectedPlan === plan.id ? 'text-green-600' : 'text-slate-900'
                        }`}>
                          {formatPrice(plan.price)}
                        </span>
                        <span className="text-sm text-slate-600">{formatBillingPeriod(plan.billingPeriod)}</span>
                      </div>
                      <p className="text-sm text-slate-600">{plan.description}</p>
                    </div>

                    {/* All Features */}
                    <div className="space-y-2.5 mb-6">
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">
                        All Features Included:
                      </p>
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check size={16} className={`flex-shrink-0 mt-0.5 ${
                            selectedPlan === plan.id ? 'text-green-600' : 'text-blue-600'
                          }`} />
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Storage & Additional Info */}
                    <div className="pt-4 border-t border-slate-200 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`p-1.5 rounded ${
                          selectedPlan === plan.id ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            selectedPlan === plan.id ? 'text-green-600' : 'text-blue-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <span className="font-semibold text-slate-700">Storage: {plan.storage}</span>
                      </div>
                      {plan.additionalUserPrice && (
                        <div className="flex items-center gap-2 text-sm">
                          <div className={`p-1.5 rounded ${
                            selectedPlan === plan.id ? 'bg-green-100' : 'bg-purple-100'
                          }`}>
                            <User size={16} className={
                              selectedPlan === plan.id ? 'text-green-600' : 'text-purple-600'
                            } />
                          </div>
                          <span className="text-slate-600">
                            Additional users: <strong className="text-slate-800">{formatPrice(plan.additionalUserPrice)}/contractor</strong>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Select Button */}
                    <button
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        setShowPlanFeaturesModal(false);
                      }}
                      className={`w-full font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] ${
                        selectedPlan === plan.id
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                      }`}
                    >
                      {selectedPlan === plan.id ? (
                        <span className="flex items-center justify-center gap-2">
                          <Check size={20} />
                          Selected
                        </span>
                      ) : (
                        'Select Plan'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
              <div className="space-y-3">
                <p className="text-xs text-slate-600">
                  💡 You can change your plan anytime in Settings after signup
                </p>
                <p className="text-xs text-slate-500 italic">
                  Payment will be charged to your Apple ID. Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
                </p>
                
                {/* Privacy Policy & Terms */}
                <div className="flex gap-2 justify-center text-xs">
                  <a 
                    href="https://noorcad.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 underline"
                  >
                    Privacy Policy
                  </a>
                  <span className="text-slate-400">•</span>
                  <a 
                    href="https://noorcad.com/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-slate-900 underline"
                  >
                    Terms of Use
                  </a>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowPlanFeaturesModal(false)}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold text-sm transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {isPasswordResetMode ? 'Reset Your Password' : 'Verify Your Email'}
                  </h2>
                  <p className="text-blue-100 text-sm mt-0.5">
                    Enter the code sent to {isPasswordResetMode ? resetEmail : pendingUserData?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Show success state OR verification form */}
              {verificationSuccess ? (
                /* Success State */
                <div className="space-y-4">
                  {/* Success Icon */}
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <Check size={32} className="text-green-600" />
                    </div>
                  </div>

                  {/* Success Message */}
                  <div className="text-center space-y-2">
                    <h3 className="text-xl font-bold text-slate-900">
                      {isPasswordResetMode ? 'Code Verified! 🎉' : 'Account Created! 🎉'}
                    </h3>
                    <p className="text-slate-600">{successMessage}</p>
                  </div>

                  {/* Password Reset Form - Only for password reset mode */}
                  {isPasswordResetMode ? (
                    <div className="space-y-4">
                      {/* Error Display for Password Reset */}
                      {verificationError && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
                          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span>{verificationError}</span>
                        </div>
                      )}
                      
                      {/* New Password */}
                      <div className="relative group">
                        <label htmlFor="newPassword" className="block text-sm font-semibold text-slate-700 mb-2">New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            disabled={verificationLoading}
                            className="appearance-none block w-full px-4 py-3 pr-12 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            disabled={verificationLoading}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-amber-600 transition-colors"
                          >
                            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                        <div className="mt-2 text-xs space-y-1">
                          <p className="font-semibold text-slate-700">Password must contain:</p>
                          <ul className="space-y-1 text-slate-600">
                            <li className={`flex items-center gap-1 ${newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                              {newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                            </li>
                            <li className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}`}>
                              {/[A-Z]/.test(newPassword) ? '✓' : '○'} One uppercase letter
                            </li>
                            <li className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-green-600' : ''}`}>
                              {/[a-z]/.test(newPassword) ? '✓' : '○'} One lowercase letter
                            </li>
                            <li className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-green-600' : ''}`}>
                              {/[0-9]/.test(newPassword) ? '✓' : '○'} One number
                            </li>
                            <li className={`flex items-center gap-1 ${/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}`}>
                              {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✓' : '○'} One special character
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* Confirm New Password */}
                      <div className="relative group">
                        <label htmlFor="confirmNewPassword" className="block text-sm font-semibold text-slate-700 mb-2">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmNewPassword ? 'text' : 'password'}
                            id="confirmNewPassword"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            required
                            disabled={verificationLoading}
                            className="appearance-none block w-full px-4 py-3 pr-12 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 sm:text-sm disabled:bg-slate-50 transition-all duration-200 bg-white"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            disabled={verificationLoading}
                            onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-amber-600 transition-colors"
                          >
                            {showConfirmNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      {/* Reset Password Button */}
                      <button
                        onClick={handleResetPassword}
                        disabled={verificationLoading || !newPassword || !confirmNewPassword}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {verificationLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Resetting password...
                          </span>
                        ) : (
                          'Reset Password'
                        )}
                      </button>
                    </div>
                  ) : (
                    /* Signup Success - Go to Login */
                    <>
                      {/* Info Box */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm text-slate-700 text-center">
                          <strong>Ready to get started?</strong>
                          <br />
                          Click below to log in with your credentials
                        </p>
                      </div>

                      {/* Go to Login Button */}
                      <button
                    onClick={async () => {
                      secureLog.info('Starting account creation');
                      setCreatingAccount(true);
                      
                      // Set flag to tell App.tsx to ignore SIGNED_IN during signup
                      sessionStorage.setItem('creatingAccount', 'true');
                      
                      // Store data before starting
                      const userData = pendingUserData;
                      const invitation = pendingInvitation;
                      const token = inviteToken;
                      
                      try {
                        // 1. Create account
                        secureLog.debug('Calling supabase.auth.signUp');
                        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                          email: userData.email,
                          password: userData.password,
                          options: {
                            data: userData.metadata,
                            emailRedirectTo: 'https://noorcad.com'
                          }
                        });
                        
                        if (signUpError) {
                          secureLog.error('Account creation error', signUpError);
                          setCreatingAccount(false);
                          return;
                        }
                        
                        if (signUpData.user) {
                          // 2. Create organization if business account
                          if (userData.metadata.account_type === 'business' && 
                              (userData.metadata.subscription_plan === 'team' || 
                               userData.metadata.subscription_plan === 'business')) {
                            try {
                              await createOrganization(
                                userData.metadata.company_name || `${userData.metadata.first_name}'s Team`,
                                userData.metadata.subscription_plan,
                                userData.metadata.company_name,
                                userData.metadata.company_registration,
                                userData.metadata.vat_number
                              );
                            } catch (orgError) {
                              secureLog.error('Organization creation error', orgError);
                            }
                          }
                          
                          // 3. Accept team invitation if present
                          if (token) {
                            try {
                              await acceptInvitation(token, signUpData.user.id);
                            } catch (inviteError) {
                              secureLog.error('Invitation acceptance error', inviteError);
                            }
                          }
                          
                          // 4. Sign out immediately
                          await supabase.auth.signOut();
                          
                          // 5. Clear flag - allow App.tsx to respond to auth events again
                          sessionStorage.removeItem('creatingAccount');
                          
                          // 6. NOW switch to login view (after everything is complete)
                          setShowVerificationModal(false);
                          setVerificationSuccess(false);
                          setIsLoginView(true);
                          
                          // Clear form data
                          clearSignupFields();
                          setVerificationCode('');
                          setPendingUserData(null);
                          setPendingInvitation(null);
                        }
                      } catch (error) {
                        secureLog.error('Account creation failed', error);
                        setCreatingAccount(false);
                        // Clear flag on error too
                        sessionStorage.removeItem('creatingAccount');
                      }
                    }}
                    disabled={creatingAccount}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingAccount ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating your account...
                      </span>
                    ) : (
                      'Go to Login →'
                    )}
                  </button>
                    </>
                  )}
                </div>
              ) : (
                /* Verification Form */
                <>
                  {verificationError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span>{verificationError}</span>
                    </div>
                  )}

              <div>
                <p className="text-sm text-slate-600 mb-4">
                  {isPasswordResetMode 
                    ? "We've sent a 6-digit verification code to your email. Enter it below to verify your identity and reset your password."
                    : "We've sent a 6-digit verification code to your email. Please enter it below to complete your registration."
                  }
                </p>

                {/* Verification Code Input */}
                <label htmlFor="verificationCode" className="block text-sm font-semibold text-slate-700 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  maxLength={6}
                  placeholder="000000"
                  disabled={verificationLoading || timeRemaining === 0}
                  className="appearance-none block w-full px-4 py-3 border-2 border-slate-200 rounded-xl shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-center text-2xl font-bold tracking-widest disabled:bg-slate-50 disabled:text-slate-500 transition-all duration-200"
                />
              </div>
              

              {/* Timer */}
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm font-semibold ${timeRemaining < 60 ? 'text-red-600' : 'text-slate-600'}`}>
                  Time remaining: {formatTime(timeRemaining)}
                </span>
              </div>

              {timeRemaining === 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
                  ⏱️ Your verification code has expired. Please request a new one.
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={handleVerifyCode}
                  disabled={verificationLoading || verificationCode.length !== 6 || timeRemaining === 0}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
                >
                  {verificationLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    isPasswordResetMode ? 'Verify Code' : 'Verify Email'
                  )}
                </button>

                <button
                  onClick={handleResendCode}
                  disabled={resendingCode || timeRemaining > 90}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {resendingCode ? 'Sending...' : timeRemaining > 90 ? 'Resend code (available soon)' : 'Resend verification code'}
                </button>

                <button
                  onClick={() => {
                    setShowVerificationModal(false);
                    setPendingUserData(null);
                    setVerificationCode('');
                    setVerificationError(null);
                    setVerificationSuccess(false);
                    // Reset password reset mode states
                    setIsPasswordResetMode(false);
                    setResetEmail('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  disabled={verificationLoading}
                  className="text-sm text-slate-600 hover:text-slate-700 font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>

              {/* Info */}
              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center">
                  💡 Didn't receive the code? Check your spam folder or click "Resend verification code"
                </p>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* IAP Payment Processing Modal */}
      {showIAPSelection && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <h2 className="text-2xl font-bold text-white">Processing Subscription</h2>
              <p className="text-blue-100 text-sm mt-1">Complete your signup</p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {successMessage && (
                <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>{successMessage}</span>
                </div>
              )}
              
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Selected Plan Display */}
              {selectedPlan && (
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg text-slate-900">
                      {selectedPlan === 'pro_trial' ? 'Pro Trial' : 
                       selectedPlan === 'pro' ? 'Professional' :
                       selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
                    </h3>
                    {selectedPlan === 'pro_trial' && (
                      <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        FREE
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    {selectedPlan === 'pro_trial' ? '14-day free trial' : 'Monthly subscription'}
                  </p>
                </div>
              )}

              {/* Apple Subscription Disclaimer */}
              <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-600 italic">
                  Payment will be charged to your Apple ID. Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
                </p>
              </div>

              {/* Loading State */}
              {(purchasingIAP || creatingAccount) && (
                <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg className="animate-spin h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm font-semibold text-blue-700">
                    {purchasingIAP ? 'Processing purchase...' : 'Creating your account...'}
                  </span>
                </div>
              )}

              {/* Cancel Button */}
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowIAPSelection(false);
                    setPendingUserData(null);
                    setError(null);
                    setSuccessMessage('');
                  }}
                  disabled={purchasingIAP || creatingAccount}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remote Logout Modal */}
      {showRemoteLogoutModal && (
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-5 rounded-t-2xl">
              <h2 className="text-2xl font-bold">Remote Logout</h2>
              <p className="text-sm text-red-100 mt-1">
                {remoteLogoutStep === 'request' ? 'Request logout from other device' : 'Enter confirmation code'}
              </p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {remoteLogoutStep === 'request' ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      ⚠️ This will log you out from all devices. A 6-digit confirmation code will be sent to your email.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={remoteLogoutEmail}
                      readOnly
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl bg-slate-100 text-slate-600 cursor-not-allowed"
                    />
                  </div>

                  {remoteLogoutError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {remoteLogoutError}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowRemoteLogoutModal(false);
                        setRemoteLogoutStep('request');
                        setRemoteLogoutEmail('');
                        setRemoteLogoutError(null);
                      }}
                      className="flex-1 px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRequestRemoteLogout}
                      disabled={remoteLogoutLoading}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold rounded-xl shadow-lg transition-all disabled:cursor-not-allowed"
                    >
                      {remoteLogoutLoading ? 'Sending...' : 'Send Code'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      📧 Check your email for the 6-digit confirmation code. 
                      {remoteLogoutTimeRemaining > 0 ? (
                        <span className="font-semibold"> Expires in {Math.floor(remoteLogoutTimeRemaining / 60)}:{String(remoteLogoutTimeRemaining % 60).padStart(2, '0')}</span>
                      ) : (
                        <span className="text-red-600 font-semibold"> Code expired</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Confirmation Code
                    </label>
                    <input
                      type="text"
                      value={remoteLogoutCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setRemoteLogoutCode(value);
                      }}
                      maxLength={6}
                      placeholder="000000"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-center text-2xl font-bold tracking-widest"
                    />
                  </div>

                  {remoteLogoutError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {remoteLogoutError}
                    </div>
                  )}

                  {/* Resend Code Button */}
                  <div className="text-center">
                    <button
                      onClick={handleResendRemoteLogoutCode}
                      disabled={resendingRemoteLogoutCode || remoteLogoutTimeRemaining > 90}
                      className="text-sm text-blue-600 hover:text-blue-800 font-semibold disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {resendingRemoteLogoutCode ? 'Resending...' : "Didn't receive the code? Resend"}
                    </button>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setRemoteLogoutStep('request');
                        setRemoteLogoutCode('');
                        setRemoteLogoutError(null);
                      }}
                      className="flex-1 px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleConfirmRemoteLogout}
                      disabled={remoteLogoutLoading || remoteLogoutCode.length !== 6}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-bold rounded-xl shadow-lg transition-all disabled:cursor-not-allowed"
                    >
                      {remoteLogoutLoading ? 'Confirming...' : 'Confirm Logout'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AuthPage; 